/* ============================================================
   HEY OATES — inline widget
   v34: handle normalization parity fix.
     - handleFromUrl() now returns a NORMALIZED handle so it matches
       the grid handles in pageContext.collectionProductHandles
       (which are already normalized in detectPageContext()).
     - This fixes the long-standing bug where products like KHAOS®
       Max-Grip weren't being highlighted because the URL contained
       %C2%AE or the slug had ® differences.
   v33: Lambda v7 consent-chip rendering.
     - When Lambda returns `consent_buttons` (Yes/No tabs),
       render them as clickable chips below the bot reply.
     - Clicking a chip writes localStorage `oates_cookies_accepted`
       and re-submits the chip's value ("yes"/"no") as the next query
       so the Lambda can finish the consent flow and answer the
       originally stashed question.
   v32: collection-page aware.
     - Detects collection page + handle, sends as page_context.
     - On collection pages: matched in-collection products are
       SUPPRESSED in the chat and highlighted in the grid instead.
     - Off-collection matches still render as cards in the chat,
       prefixed with a friendly "you may be looking for" message.
     - On homepage / PDP / everywhere else: identical behavior to v31.
   v30: removed capture-phase stopPropagation that killed submit.
   v27: reusable across homepage + collection pages.
   ============================================================ */
(function () {
  'use strict';

  const API_URL = 'https://vnpc4c3fs4.execute-api.us-east-1.amazonaws.com/prod/query';

  // ---- Page context detection ----------------------------------
  // Determine the current page type + (if collection) the collection handle.
  // Reads from a Shopify-rendered <meta> if present, falls back to URL inspection.
  function detectPageContext() {
    const out = { type: 'other', collectionHandle: null, collectionProductHandles: [] };

    // Prefer an explicit meta tag rendered by the theme (most reliable)
    const meta = document.querySelector('meta[name="oates:page-context"]');
    if (meta) {
      out.type = meta.dataset.pageType || meta.getAttribute('data-page-type') || 'other';
      out.collectionHandle = meta.dataset.collectionHandle || meta.getAttribute('data-collection-handle') || null;
    }

    // Fallback: URL-based detection
    if (out.type === 'other') {
      const path = window.location.pathname;
      if (path === '/' || path === '') out.type = 'homepage';
      else if (path.indexOf('/collections/') === 0 && path.indexOf('/products/') === -1) {
        out.type = 'collection';
        const m = path.match(/\/collections\/([^\/\?#]+)/);
        if (m) out.collectionHandle = m[1];
      } else if (path.indexOf('/products/') !== -1) out.type = 'product';
    }

    // Capture handles of every product currently rendered in the collection grid.
    // This is what we'll match against to decide in-collection vs off-collection.
    if (out.type === 'collection') {
      // v33.1: try the strict markup first, but FALL BACK to any product
      // card on the page so themes without data-collection-handle still work.
      let grid = document.querySelector('.collection-grid[data-collection-handle]');
      if (!grid) grid = document.querySelector('.collection-grid, [data-collection], main');
      if (grid) {
        out.collectionHandle = out.collectionHandle ||
          grid.getAttribute('data-collection-handle') ||
          grid.getAttribute('data-collection') || null;
        // Try multiple attribute conventions Shopify themes use
        const cards = grid.querySelectorAll('[data-product-handle], [data-handle], [data-product-id]');
        out.collectionProductHandles = Array.from(cards)
          .map(function (el) {
            let v = el.getAttribute('data-product-handle') || el.getAttribute('data-handle');
            if (!v) {
              // Last resort — read from any product link inside the card
              const a = el.querySelector('a[href*="/products/"]');
              if (a) {
                const m = a.getAttribute('href').match(/\/products\/([^\/\?#]+)/);
                if (m) {
                  try { v = decodeURIComponent(m[1]); } catch (e) { v = m[1]; }
                }
              }
            }
            if (!v) return null;
            return v.replace(/[\u00ae\u2122\u00a9]/g, '').replace(/--+/g, '-').replace(/^-|-$/g, '').toLowerCase();
          })
          .filter(Boolean);
        console.log('[hey-oates v33.1] collection grid found ' + out.collectionProductHandles.length + ' product handles', out.collectionProductHandles.slice(0, 5));
      } else {
        console.warn('[hey-oates v33.1] collection page detected but no product grid found');
      }
    }

    return out;
  }

  const pageContext = detectPageContext();

  // ---- Helpers shared across all instances ----------------------
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  // Normalize a handle the SAME way the collection bridge does so encoded
  // ® / ™ / ©, case, and double-dashes don't break equality.
  function normalizeHandle(h) {
    if (!h) return '';
    try { h = decodeURIComponent(h); } catch (e) {}
    return h.replace(/[\u00ae\u2122\u00a9]/g, '').replace(/--+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  }

  // Extract a product handle from a Shopify product URL.
  // Always returns a NORMALIZED handle (decoded, lowercased, ® stripped) so
  // it can be compared 1:1 with grid handles, which are also normalized.
  function handleFromUrl(url) {
    if (!url) return null;
    const m = String(url).match(/\/products\/([^\/\?#]+)/);
    return m ? normalizeHandle(m[1]) : null;
  }

  // Raw (un-normalized) extractor for cases where we need the exact URL slug
  // (currently unused, kept for future debugging).
  function rawHandleFromUrl(url) {
    if (!url) return null;
    const m = String(url).match(/\/products\/([^\/\?#]+)/);
    return m ? m[1] : null;
  }

  function renderProducts(products, opts) {
    if (!products || !products.length) return '';
    opts = opts || {};
    const label = opts.label ? '<p class="ho-products__label">' + escapeHtml(opts.label) + '</p>' : '';
    const items = products.slice(0, 3).map(function (p) {
      const url = p.url || '#';
      const img = p.image || '';
      const name = escapeHtml(p.name || 'Product');
      const price = p.price ? '$' + escapeHtml(String(p.price)) : '';
      return (
        '<a class="ho-product" href="' + escapeHtml(url) + '" target="_blank" rel="noopener">' +
          (img ? '<img class="ho-product__img" src="' + escapeHtml(img) + '" alt="" loading="lazy" />' : '') +
          '<div class="ho-product__body">' +
            '<p class="ho-product__name">' + name + '</p>' +
            (price ? '<p class="ho-product__price">' + price + '</p>' : '') +
          '</div>' +
          '<span class="ho-product__cta">View &rsaquo;</span>' +
        '</a>'
      );
    }).join('');
    return '<div class="ho-products">' + label + items + '</div>';
  }

  // Stable per-tab session id
  let sessionId = null;
  try {
    sessionId = sessionStorage.getItem('hey_oates_session');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
      sessionStorage.setItem('hey_oates_session', sessionId);
    }
  } catch (e) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
  }

  let cookiesAccepted = false;
  try { cookiesAccepted = localStorage.getItem('oates_cookies_accepted') === 'true'; } catch (e) {}

  // ---- Per-widget initializer ----------------------------------
  function initWidget(root) {
    if (!root || root.dataset.hoInit === '1') return;
    root.dataset.hoInit = '1';

    const transcript = root.querySelector('[data-ho-transcript]');
    const form       = root.querySelector('[data-ho-form]');
    const input      = root.querySelector('[data-ho-input]');
    const sendBtn    = root.querySelector('[data-ho-send]');
    const quickstart = root.querySelector('[data-ho-quickstart]');
    const context    = root.dataset.hoContext || 'inline_widget';

    if (!transcript || !form || !input) return;

    const shownProductIds = [];

    function addMessage(role, htmlContent) {
      const wrap = document.createElement('div');
      wrap.className = 'ho-msg ho-msg--' + (role === 'user' ? 'user' : 'bot');
      const bubble = document.createElement('div');
      bubble.className = 'ho-bubble';
      bubble.innerHTML = htmlContent;
      wrap.appendChild(bubble);
      transcript.appendChild(wrap);
      transcript.scrollTop = transcript.scrollHeight;
      return wrap;
    }

    function addTyping() {
      const wrap = document.createElement('div');
      wrap.className = 'ho-msg ho-msg--bot ho-typing-row';
      wrap.innerHTML = '<div class="ho-bubble"><div class="ho-typing"><span></span><span></span><span></span></div></div>';
      transcript.appendChild(wrap);
      transcript.scrollTop = transcript.scrollHeight;
      return wrap;
    }
    function removeTyping() {
      const t = transcript.querySelector('.ho-typing-row');
      if (t) t.remove();
    }

    async function askHeyOates(query) {
      if (!query || !query.trim()) return;
      const cleanQuery = query.trim().slice(0, 500);

      if (quickstart && !quickstart.dataset.hidden) {
        quickstart.style.display = 'none';
        quickstart.dataset.hidden = 'true';
      }

      addMessage('user', escapeHtml(cleanQuery));
      input.value = '';
      input.disabled = true;
      if (sendBtn) sendBtn.disabled = true;
      addTyping();

      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: cleanQuery,
            session_id: sessionId,
            cookie_accepted: cookiesAccepted,
            previous_products: shownProductIds,
            context: context,
            page_context: {
              type: pageContext.type,
              collection_handle: pageContext.collectionHandle,
              // v34: also send the list of product handles currently in the grid
              // so Lambda can boost in-collection products in its ranking.
              collection_product_handles: pageContext.collectionProductHandles || []
            }
          })
        });

        const data = await res.json();
        removeTyping();

        const reply = (data && data.response) || "I'm having trouble right now. Please call or text our team at (936) 295-4459 and we'll point you to the right gear.";
        const products = (data && data.products) || [];

        products.forEach(function (p) {
          const id = p.sku || p.name;
          if (id && shownProductIds.indexOf(id) === -1) shownProductIds.push(id);
        });

        // ───────────────────────────────────────────────
        // COLLECTION-PAGE SPLIT LOGIC
        // ───────────────────────────────────────────────
        // If we're on a collection page, split products into:
        //   - inCollection:  appear in the current grid (highlight + reorder)
        //   - offCollection: not in current grid (still render in chat with label)
        let inCollection = [];
        let offCollection = products;

        if (pageContext.type === 'collection' && products.length > 0) {
          // v33.1 debug — expose every comparison in the console so handle
          // mismatches are easy to diagnose without re-reading the theme.
          const productHandles = products.map(function (p) { return { name: p.name, h: handleFromUrl(p.url) }; });
          console.log('[hey-oates v34] bot returned', productHandles.length, 'products. Comparing to', pageContext.collectionProductHandles.length, 'grid handles.');
          console.log('[hey-oates v34] returned handles (normalized):', productHandles);
          console.log('[hey-oates v34] grid handles (normalized):', pageContext.collectionProductHandles);

          inCollection = products.filter(function (p) {
            const h = handleFromUrl(p.url);
            return h && pageContext.collectionProductHandles.indexOf(h) !== -1;
          });
          offCollection = products.filter(function (p) {
            const h = handleFromUrl(p.url);
            return !(h && pageContext.collectionProductHandles.indexOf(h) !== -1);
          });
          console.log('[hey-oates v34] inCollection:', inCollection.length, 'offCollection:', offCollection.length);
          if (inCollection.length === 0 && offCollection.length > 0) {
            // Helpful when nothing matched — show which handles missed.
            console.warn('[hey-oates v34] NO matches against current grid. Returned handles:',
              products.map(function (p) { return handleFromUrl(p.url); }));
          }

          // Fire the bridge event so the collection grid can highlight + reorder
          if (inCollection.length > 0) {
            const handles = inCollection.map(function (p) { return handleFromUrl(p.url); }).filter(Boolean);
            document.dispatchEvent(new CustomEvent('oates:bot-match', {
              detail: { handles: handles, query: cleanQuery }
            }));
          }
        }

        // Build response HTML
        let html = '<p>' + escapeHtml(reply).replace(/\n/g, '<br>') + '</p>';

        // On collection pages, suppress in-chat cards for in-collection matches
        // (the grid will highlight them instead) — but keep off-collection cards
        // and prefix with a friendly label.
        if (pageContext.type === 'collection') {
          if (inCollection.length > 0) {
            html += '<p class="ho-collection-note"><strong>✨ Highlighted ' +
                    inCollection.length + ' product' + (inCollection.length > 1 ? 's' : '') +
                    ' below</strong> matching your question.</p>';
          }
          if (offCollection.length > 0) {
            html += renderProducts(offCollection, {
              label: inCollection.length > 0
                ? 'You may also be looking for these (in another collection):'
                : "While these aren't in this collection, you may be looking for these:"
            });
          }
        } else {
          // Homepage / PDP / everywhere else — original behavior, all cards in chat
          html += renderProducts(products);
        }

        if (data && data.requiresEscalation) {
          html += '<p style="margin-top:10px;"><a href="tel:+19362954459">Call or text (936) 295-4459</a></p>';
        }

        // v33: render consent chips if Lambda asked for cookie/save consent
        if (data && Array.isArray(data.consent_buttons) && data.consent_buttons.length > 0) {
          html += '<div class="ho-consent-chips" data-ho-consent-chips>';
          data.consent_buttons.forEach(function (btn) {
            const val = String(btn.value || '').toLowerCase();
            const label = escapeHtml(btn.label || (val === 'yes' ? 'Yes' : 'No'));
            html += '<button type="button" class="ho-chip ho-consent-chip" data-consent-value="' + escapeHtml(val) + '">' + label + '</button>';
          });
          html += '</div>';
        }

        addMessage('bot', html);

      } catch (err) {
        removeTyping();
        addMessage('bot',
          '<p>I had a connection problem. Please try again, or <a href="tel:+19362954459">call or text our team at (936) 295-4459</a>.</p>'
        );
        if (window.console) console.warn('Hey Oates error:', err);
      } finally {
        input.disabled = false;
        if (sendBtn) sendBtn.disabled = false;
        input.focus();
      }
    }

    // Bindings — bubble phase, single preventDefault (no stopPropagation; v30 fix)
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      askHeyOates(input.value);
      return false;
    });

    if (quickstart) {
      quickstart.addEventListener('click', function (e) {
        const btn = e.target.closest('.ho-chip');
        if (!btn) return;
        e.preventDefault();
        const q = btn.dataset.q || btn.textContent.trim();
        askHeyOates(q);
      });
    }

    // v33: delegated handler for consent-chip clicks inside the transcript.
    // Writes localStorage flag then submits the chip's value as the next query
    // so the Lambda can finish the consent flow and answer the stashed question.
    transcript.addEventListener('click', function (e) {
      const chip = e.target.closest('.ho-consent-chip');
      if (!chip) return;
      e.preventDefault();
      const val = (chip.dataset.consentValue || '').toLowerCase();
      if (val === 'yes') {
        try { localStorage.setItem('oates_cookies_accepted', 'true'); } catch (e2) {}
        cookiesAccepted = true;
      } else if (val === 'no') {
        try { localStorage.setItem('oates_cookies_accepted', 'false'); } catch (e2) {}
        cookiesAccepted = false;
      }
      // Disable both chips in this group so user can't double-tap
      const group = chip.closest('[data-ho-consent-chips]');
      if (group) {
        Array.prototype.forEach.call(group.querySelectorAll('.ho-consent-chip'), function (c) {
          c.disabled = true;
          if (c === chip) c.classList.add('ho-consent-chip--selected');
        });
      }
      askHeyOates(val);
    });
  }

  // ---- Auto-init all widgets on the page -----------------------
  function initAll() {
    const widgets = document.querySelectorAll('.hey-oates-widget');
    widgets.forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Expose for theme editor re-rendering + debugging
  window.OatesHeyOates = {
    initAll: initAll,
    initWidget: initWidget,
    pageContext: pageContext
  };
})();
